import { useState, useCallback } from "react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useLocation } from "wouter";
import {
  useGetMe, useListCompanies, useListUsers,
  useCreateCompany, useUpdateCompany, useCreateUser, useLogout, useSelectCompany,
} from "@workspace/api-client-react";
import type { Company } from "@workspace/api-client-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Building2, CreditCard, Users, Settings,
  LogOut, Plus, CheckCircle2,
  XCircle, Clock, Star, Zap, Package,
  ToggleLeft, ToggleRight, Edit2, LogIn, Lock, TrendingUp,
  Save, X, RefreshCw, Sliders, Shield, Globe, Trash2,
  PanelLeftClose, PanelLeft, MessageSquare, Activity,
  BarChart3, ChevronRight
} from "lucide-react";
import CRMLeads from "./CRMLeads";
import CPDashboard from "./CPDashboard";
import CPSystemAdmin from "./CPSystemAdmin";
import CPChatInbox from "./CPChatInbox";
import SubscriptionEditor from "./SubscriptionEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { uid } from "@/lib/finance";
import { PLANS, ALL_FEATURE_KEYS, FEATURE_LABELS, FEATURE_GROUPS, getPlan, type PlanDef, type PlanFeatureKey } from "@/lib/plans";
import { COUNTRIES, CURRENCIES as ISO_CURRENCIES, getCurrencySymbol } from "@/lib/iso";

const PLAN_COLORS: { label: string; bg: string; badge: string }[] = [
  { label: "Blue",    bg: "bg-blue-600",    badge: "bg-blue-500/10 text-blue-600"    },
  { label: "Indigo",  bg: "bg-indigo-600",  badge: "bg-indigo-500/10 text-indigo-600" },
  { label: "Violet",  bg: "bg-violet-600",  badge: "bg-violet-500/10 text-violet-600" },
  { label: "Emerald", bg: "bg-emerald-600", badge: "bg-emerald-500/10 text-emerald-700" },
  { label: "Teal",    bg: "bg-teal-600",    badge: "bg-teal-500/10 text-teal-700"    },
  { label: "Sky",     bg: "bg-sky-500",     badge: "bg-sky-500/10 text-sky-700"      },
  { label: "Amber",   bg: "bg-amber-500",   badge: "bg-amber-500/10 text-amber-700"  },
  { label: "Orange",  bg: "bg-orange-500",  badge: "bg-orange-500/10 text-orange-700" },
  { label: "Rose",    bg: "bg-rose-500",    badge: "bg-rose-500/10 text-rose-600"    },
  { label: "Slate",   bg: "bg-slate-500",   badge: "bg-slate-400/10 text-slate-500"  },
];

type PlanConfig = {
  planId: string; label: string | null; maxProjects: number | null;
  maxUsers: number | null; priceMonthly: number | null; priceYearly: number | null;
  yearlyDiscountPct: number; priceLabel: string | null; description: string | null;
  features: Partial<Record<PlanFeatureKey, boolean>> | null; highlights: string[] | null;
  color: string | null; badgeCls: string | null; popular: boolean; sortOrder: number; isActive: boolean;
};

const BUILT_IN_PLAN_IDS = new Set(["lite", "lite-pro", "pro", "pro-plus"]);

async function fetchPlanConfigs(): Promise<PlanConfig[]> {
  const res = await fetch("/api/admin/plan-configs", { credentials: "include" });
  if (!res.ok) return [];
  return res.json() as Promise<PlanConfig[]>;
}

async function createPlanConfig(data: {
  planId: string; label: string; maxProjects: number | null; maxUsers: number | null;
  priceMonthly: number | null; priceYearly: number | null; yearlyDiscountPct: number;
  priceLabel: string | null; description: string | null;
  features: Partial<Record<PlanFeatureKey, boolean>>; highlights: string[];
  color: string; badgeCls: string; popular: boolean; sortOrder: number;
}): Promise<void> {
  const res = await fetch("/api/admin/plan-configs", {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ planId: data.planId, ...data }),
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? "Failed to create plan"); }
}

async function deletePlanConfig(planId: string): Promise<void> {
  const res = await fetch(`/api/admin/plan-configs/${planId}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? "Failed to delete plan"); }
}

async function savePlanConfig(planId: string, data: Partial<PlanConfig>): Promise<PlanConfig> {
  const res = await fetch(`/api/admin/plan-configs/${planId}`, {
    method: "PUT", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      label: data.label, maxProjects: data.maxProjects, maxUsers: data.maxUsers,
      priceMonthly: data.priceMonthly, priceYearly: data.priceYearly,
      yearlyDiscountPct: data.yearlyDiscountPct, priceLabel: data.priceLabel,
      description: data.description, features: data.features, highlights: data.highlights,
      color: data.color, badgeCls: data.badgeCls,
    }),
  });
  if (!res.ok) throw new Error("Failed to save plan config");
  return res.json() as Promise<PlanConfig>;
}

function mergePlan(base: PlanDef, cfg: PlanConfig | undefined): PlanDef {
  if (!cfg) return base;
  return {
    ...base,
    label: cfg.label ?? base.label,
    color: cfg.color ?? base.color,
    badgeCls: cfg.badgeCls ?? base.badgeCls,
    maxProjects: cfg.maxProjects ?? base.maxProjects,
    maxUsers: cfg.maxUsers ?? base.maxUsers,
    price: cfg.priceLabel ?? base.price,
    priceMonthly: cfg.priceMonthly ?? base.priceMonthly,
    priceYearly: cfg.priceYearly ?? base.priceYearly,
    yearlyDiscountPct: cfg.yearlyDiscountPct ?? base.yearlyDiscountPct,
    description: cfg.description ?? base.description,
    features: cfg.features ? { ...base.features, ...cfg.features } : base.features,
    highlights: cfg.highlights ?? base.highlights,
  };
}

function ColorPicker({ value, onChange }: { value: string; onChange: (bg: string, badge: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PLAN_COLORS.map(c => (
        <button key={c.bg} type="button" title={c.label}
          onClick={() => onChange(c.bg, c.badge)}
          className={`w-8 h-8 rounded-lg ${c.bg} transition-all shrink-0 ${value === c.bg ? "ring-2 ring-offset-2 ring-gray-700 scale-110" : "opacity-70 hover:opacity-100 hover:scale-105"}`}
        />
      ))}
    </div>
  );
}

function FeatureToggles({ features, onToggle }: {
  features: Record<PlanFeatureKey, boolean>; onToggle: (key: PlanFeatureKey) => void;
}) {
  const enabledCount = ALL_FEATURE_KEYS.filter(k => features[k]).length;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{enabledCount} of {ALL_FEATURE_KEYS.length} enabled</span>
        <div className="flex gap-2">
          <button type="button" className="text-xs text-blue-600 hover:underline"
            onClick={() => ALL_FEATURE_KEYS.forEach(k => !features[k] && onToggle(k))}>All on</button>
          <span className="text-gray-300">·</span>
          <button type="button" className="text-xs text-gray-400 hover:underline"
            onClick={() => ALL_FEATURE_KEYS.forEach(k => features[k] && onToggle(k))}>All off</button>
        </div>
      </div>
      {FEATURE_GROUPS.map(group => (
        <div key={group.label} className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{group.label}</span>
          </div>
          {group.keys.map((key, i) => (
            <div key={key}
              className={`flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${i > 0 ? "border-t border-gray-100" : ""}`}
              onClick={() => onToggle(key)}
            >
              <span className={`text-sm ${features[key] ? "text-gray-900 font-medium" : "text-gray-400"}`}>
                {FEATURE_LABELS[key]}
              </span>
              <div className={`relative inline-flex h-5 w-9 rounded-full transition-colors shrink-0 ${features[key] ? "bg-blue-600" : "bg-gray-200"}`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5 ${features[key] ? "translate-x-4.5" : "translate-x-0.5"}`} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PlanEditor({ plan, config, onClose, onSaved }: {
  plan: PlanDef; config: PlanConfig | undefined; onClose: () => void; onSaved: () => void;
}) {
  const merged = mergePlan(plan, config);
  const [color, setColor] = useState(merged.color);
  const [badge, setBadge] = useState(merged.badgeCls);
  const [features, setFeatures] = useState<Record<PlanFeatureKey, boolean>>(
    () => ({ ...Object.fromEntries(ALL_FEATURE_KEYS.map(k => [k, false])), ...merged.features }) as Record<PlanFeatureKey, boolean>
  );
  const [saving, setSaving] = useState(false);
  const toggleFeature = (key: PlanFeatureKey) => setFeatures(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    try {
      await savePlanConfig(plan.id, {
        label: (fd.get("label") as string) || null,
        maxProjects: fd.get("maxProjects") ? parseInt(fd.get("maxProjects") as string) : null,
        maxUsers: fd.get("maxUsers") ? parseInt(fd.get("maxUsers") as string) : null,
        priceMonthly: fd.get("priceMonthly") ? parseFloat(fd.get("priceMonthly") as string) : null,
        priceYearly: fd.get("priceYearly") ? parseFloat(fd.get("priceYearly") as string) : null,
        yearlyDiscountPct: parseInt(fd.get("yearlyDiscountPct") as string) || 0,
        priceLabel: (fd.get("priceLabel") as string) || null,
        description: (fd.get("description") as string) || null,
        features,
        highlights: (fd.get("highlights") as string || "").split("\n").map(l => l.trim()).filter(Boolean),
        color,
        badgeCls: badge,
      });
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-bold text-lg flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg ${color} flex items-center justify-center`}>
              <Edit2 className="w-4 h-4 text-white" />
            </div>
            Edit {plan.label}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-5 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Display Label</Label>
              <Input name="label" defaultValue={merged.label} className="text-sm rounded-xl h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Description</Label>
              <Input name="description" defaultValue={merged.description || ""} className="text-sm rounded-xl h-9" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-700">Plan Color</Label>
            <ColorPicker value={color} onChange={(bg, b) => { setColor(bg); setBadge(b); }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Monthly Price (USD)</Label>
              <Input name="priceMonthly" type="number" min="0" step="0.01" defaultValue={merged.priceMonthly ?? ""} className="text-sm rounded-xl h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Price Label</Label>
              <Input name="priceLabel" defaultValue={merged.price || ""} className="text-sm rounded-xl h-9" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Yearly Discount %</Label>
              <Input name="yearlyDiscountPct" type="number" min="0" max="100" defaultValue={merged.yearlyDiscountPct ?? 0} className="text-sm rounded-xl h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Yearly Price ($)</Label>
              <Input name="priceYearly" type="number" min="0" step="0.01" defaultValue={merged.priceYearly ?? ""} className="text-sm rounded-xl h-9" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Max Projects</Label>
              <Input name="maxProjects" type="number" min="-1" defaultValue={merged.maxProjects === -1 ? "" : merged.maxProjects} placeholder="Blank = unlimited" className="text-sm rounded-xl h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Max Users</Label>
              <Input name="maxUsers" type="number" min="-1" defaultValue={merged.maxUsers === -1 ? "" : merged.maxUsers} placeholder="Blank = unlimited" className="text-sm rounded-xl h-9" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700">Highlights (one per line)</Label>
            <textarea name="highlights" rows={3} defaultValue={merged.highlights.join("\n")} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-700">Features</Label>
            <FeatureToggles features={features} onToggle={toggleFeature} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1 rounded-xl gap-2" disabled={saving}>
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const CURRENCIES = ISO_CURRENCIES;
const ROLES = ["Admin", "Manager", "Accountant", "Viewer", "Super Admin"];

const TABS = [
  { id: "dashboard",  label: "Dashboard",     icon: BarChart3,      group: "main" },
  { id: "companies",  label: "Companies",     icon: Building2,      group: "main" },
  { id: "plans",      label: "Plans",         icon: CreditCard,     group: "main" },
  { id: "users",      label: "Users",         icon: Users,          group: "main" },
  { id: "leads",      label: "CRM / Leads",   icon: TrendingUp,     group: "main" },
  { id: "chat",       label: "Chat Inbox",    icon: MessageSquare,  group: "support" },
  { id: "system",     label: "System Admin",  icon: Shield,         group: "admin" },
  { id: "settings",   label: "Settings",      icon: Settings,       group: "admin" },
];

const GROUP_LABELS: Record<string, string> = {
  main: "Platform",
  support: "Support",
  admin: "Administration",
};

function planLabel(id: string) { return getPlan(id).label; }
function planBadge(id: string) { return getPlan(id).badgeCls; }

function StatusBadge({ status }: { status: string }) {
  const colors = status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "Suspended" ? "bg-rose-50 text-rose-600 border-rose-200"
    : "bg-gray-100 text-gray-600 border-gray-200";
  return <span className={`text-xs px-2 py-1 rounded-full font-medium border ${colors}`}>{status}</span>;
}

function PlanIcon({ id, cls }: { id: string; cls: string }) {
  if (id === "lite") return <Package className={cls} />;
  if (id === "lite-pro") return <Zap className={cls} />;
  if (id === "pro") return <Star className={cls} />;
  if (id === "pro-plus") return <Star className={cls} />;
  return <Package className={cls} />;
}

export default function ControlPanel() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data: user } = useGetMe();
  const { data: companies = [] } = useListCompanies();
  const { data: users = [] } = useListUsers({});
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const createUser = useCreateUser();
  const selectCompany = useSelectCompany();
  const logout = useLogout();

  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [editSubOpen, setEditSubOpen] = useState<Company | null>(null);
  const [editPlanOpen, setEditPlanOpen] = useState<PlanDef | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [companySearch, setCompanySearch] = useState("");
  const [bulkPlan, setBulkPlan] = useState<string>("");
  const [bulkPlanOpen, setBulkPlanOpen] = useState(false);

  const { data: planConfigs = [], refetch: refetchPlanConfigs } = useQuery({
    queryKey: ["plan-configs"],
    queryFn: fetchPlanConfigs,
    enabled: activeTab === "plans",
  });

  type CompanyUsage = Record<string, { projects: number; members: number; invoices: number }>;
  const { data: companyUsage = {} as CompanyUsage } = useQuery<CompanyUsage>({
    queryKey: ["cp-company-usage"],
    queryFn: async () => {
      const r = await fetch("/api/cp/company-usage", { credentials: "include" });
      return r.ok ? r.json() as Promise<CompanyUsage> : {};
    },
    enabled: activeTab === "companies",
    staleTime: 30_000,
  });

  const handleBulkStatusChange = async (newStatus: "Active" | "Suspended") => {
    const ids = Array.from(selectedCompanies);
    await Promise.all(ids.map(id => updateCompany.mutateAsync({ id, data: { status: newStatus } })));
    setSelectedCompanies(new Set());
  };

  const handleBulkPlanChange = async () => {
    if (!bulkPlan) return;
    const ids = Array.from(selectedCompanies);
    await Promise.all(ids.map(id => updateCompany.mutateAsync({ id, data: { plan: bulkPlan } })));
    setSelectedCompanies(new Set());
    setBulkPlanOpen(false);
    setBulkPlan("");
  };

  const filteredCompanies = companies.filter(c =>
    !companySearch ||
    c.name.toLowerCase().includes(companySearch.toLowerCase()) ||
    c.id.toLowerCase().includes(companySearch.toLowerCase()) ||
    (c.country || "").toLowerCase().includes(companySearch.toLowerCase())
  );
  const getPlanConfig = useCallback(
    (planId: string) => planConfigs.find(c => c.planId === planId),
    [planConfigs],
  );
  const getEffectivePlan = useCallback(
    (plan: PlanDef) => mergePlan(plan, getPlanConfig(plan.id)),
    [getPlanConfig],
  );

  const handleLogout = async () => {
    await logout.mutateAsync();
    qc.clear();
    setLocation("/");
  };

  const handleCreateCompany = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await createCompany.mutateAsync({
      data: {
        id: `CO-${uid()}`, name: fd.get("name") as string,
        country: fd.get("country") as string, currency: fd.get("currency") as string,
        currencySymbol: getCurrencySymbol(fd.get("currency") as string),
        plan: fd.get("plan") as string || "pro", status: "Active",
      }
    });
    setNewCompanyOpen(false);
  };

  const handleCreateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await createUser.mutateAsync({
      data: {
        id: `US-${uid()}`,
        companyId: (() => { const v = fd.get("companyId") as string; return v === "__none__" ? null : v || null; })(),
        name: fd.get("name") as string, email: fd.get("email") as string,
        pwd: fd.get("pwd") as string, role: fd.get("role") as string, status: "Active",
      }
    });
    setNewUserOpen(false);
  };

  const currentTab = TABS.find(t => t.id === activeTab);

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <CPDashboard />;
      case "chat": return <CPChatInbox />;
      case "system": return <CPSystemAdmin />;
      case "leads": return <CRMLeads />;

      case "companies": return (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Companies</h2>
              <p className="text-sm text-gray-500 mt-1">
                {filteredCompanies.length} of {companies.length} tenants
                {selectedCompanies.size > 0 && <span className="ml-2 text-blue-600 font-medium">· {selectedCompanies.size} selected</span>}
              </p>
            </div>
            <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl text-white shadow-lg shadow-blue-500/20">
                  <Plus className="w-4 h-4 mr-2" /> New Company
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader><DialogTitle className="font-bold text-lg">Register Company</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateCompany} className="space-y-4 pt-2">
                  <div className="space-y-2"><Label>Company Name *</Label><Input name="name" required className="rounded-xl" placeholder="Acme Construction Ltd." /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Select name="country" defaultValue="United States">
                        <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select name="currency" defaultValue="USD">
                        <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <Select name="plan" defaultValue="pro">
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLANS.map(p => <SelectItem key={p.id} value={p.id}>{p.label} — {p.price}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white" disabled={createCompany.isPending}>
                    {createCompany.isPending ? "Registering..." : "Register Company"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search + bulk action bar */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-52">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input value={companySearch} onChange={e => setCompanySearch(e.target.value)}
                placeholder="Search by name, ID, or country…"
                className="pl-9 rounded-xl text-sm h-9 bg-white" />
            </div>
            {selectedCompanies.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {selectedCompanies.size} selected
                <span className="w-px h-4 bg-blue-200 mx-1" />
                <button className="text-emerald-700 hover:text-emerald-900 font-medium text-xs" onClick={() => handleBulkStatusChange("Active")}>Activate</button>
                <button className="text-rose-600 hover:text-rose-800 font-medium text-xs" onClick={() => handleBulkStatusChange("Suspended")}>Suspend</button>
                {!bulkPlanOpen ? (
                  <button className="text-blue-700 hover:text-blue-900 font-medium text-xs" onClick={() => setBulkPlanOpen(true)}>Change Plan</button>
                ) : (
                  <div className="flex items-center gap-1">
                    <Select value={bulkPlan} onValueChange={setBulkPlan}>
                      <SelectTrigger className="h-7 w-40 text-xs rounded-lg border-blue-200"><SelectValue placeholder="Select plan…" /></SelectTrigger>
                      <SelectContent>{PLANS.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <button className="text-blue-700 hover:text-blue-900 font-semibold text-xs" onClick={handleBulkPlanChange} disabled={!bulkPlan}>Apply</button>
                    <button className="text-gray-400 hover:text-gray-600 text-xs" onClick={() => { setBulkPlanOpen(false); setBulkPlan(""); }}>✕</button>
                  </div>
                )}
                <button className="text-gray-400 hover:text-gray-600 text-xs ml-1" onClick={() => setSelectedCompanies(new Set())}>Clear</button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="w-10 px-4">
                    <input type="checkbox" className="rounded"
                      checked={filteredCompanies.length > 0 && filteredCompanies.every(c => selectedCompanies.has(c.id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedCompanies(new Set(filteredCompanies.map(c => c.id)));
                        else setSelectedCompanies(new Set());
                      }} />
                  </TableHead>
                  <TableHead className="text-gray-500 font-medium">Company</TableHead>
                  <TableHead className="text-gray-500 font-medium">Plan</TableHead>
                  <TableHead className="text-gray-500 font-medium text-center">Projects</TableHead>
                  <TableHead className="text-gray-500 font-medium text-center">Members</TableHead>
                  <TableHead className="text-gray-500 font-medium text-center">Invoices</TableHead>
                  <TableHead className="text-gray-500 font-medium">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-gray-400">
                    {companySearch ? "No companies match your search." : "No companies registered yet."}
                  </TableCell></TableRow>
                )}
                {filteredCompanies.map(c => {
                  const usage = companyUsage[c.id] ?? { projects: 0, members: 0, invoices: 0 };
                  const isSelected = selectedCompanies.has(c.id);
                  return (
                    <TableRow key={c.id} className={`border-gray-100 hover:bg-gray-50 group ${isSelected ? "bg-blue-50/50" : ""}`}>
                      <TableCell className="px-4">
                        <input type="checkbox" className="rounded" checked={isSelected}
                          onChange={e => {
                            const next = new Set(selectedCompanies);
                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                            setSelectedCompanies(next);
                          }} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: c.color || '#3b82f6' }}>{c.name[0]}</div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{c.name}</span>
                              {c.isDemo && (
                                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-amber-400/15 text-amber-400 border border-amber-400/20">Demo</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">
                              {c.country} · {c.currency}
                              {c.isDemo && c.demoExpiresAt && (
                                <span className="ml-2 text-amber-600">· exp {new Date(c.demoExpiresAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${planBadge(c.plan)}`}>{planLabel(c.plan)}</span>
                          {c.discountPct ? (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold bg-rose-400/15 text-rose-400 border border-rose-400/20">-{c.discountPct}%</span>
                          ) : null}
                          {c.customPrice ? (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold bg-violet-400/15 text-violet-400 border border-violet-400/20">${parseFloat(c.customPrice).toFixed(0)}/mo</span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm font-semibold text-gray-900">{usage.projects}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm font-semibold text-gray-900">{usage.members}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm font-semibold text-gray-900">{usage.invoices}</span>
                      </TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-8 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 gap-1"
                            onClick={async () => {
                              const res = await selectCompany.mutateAsync({ data: { companyId: c.id } });
                              qc.clear(); setLocation(res.redirect || `/app/${c.slug || c.id}`);
                            }}
                          ><LogIn className="w-3.5 h-3.5" /> Enter</Button>
                          <Button variant="ghost" size="sm" className="h-8 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-50 gap-1"
                            onClick={() => setEditSubOpen(c)}
                          ><Edit2 className="w-3 h-3" /> Subscription</Button>
                          <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500 hover:text-gray-900 gap-1"
                            onClick={async () => {
                              const newStatus = c.status === "Active" ? "Suspended" : "Active";
                              const action = newStatus === "Suspended" ? "Suspend" : "Activate";
                              if (await confirm({ title: `${action} Company`, message: `${action} "${c.name}"?`, confirmLabel: action, destructive: false }))
                                await updateCompany.mutateAsync({ id: c.id, data: { status: newStatus } });
                            }}
                          >
                            {c.status === "Active" ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                            {c.status === "Active" ? "Active" : "Inactive"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      );

      case "plans": return (
        <div className="space-y-8 animate-in fade-in duration-300">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Plans & Features</h2>
            <p className="text-sm text-gray-500 mt-1">Click a plan card to edit it. Toggle features in the matrix below.</p>
          </div>

          <div className="grid grid-cols-3 gap-5">
            {PLANS.map(plan => {
              const eff = getEffectivePlan(plan);
              const count = companies.filter(c => (c.plan || "pro") === plan.id).length;
              const active = companies.filter(c => (c.plan || "pro") === plan.id && c.status === "Active").length;
              const featCount = ALL_FEATURE_KEYS.filter(k => eff.features[k]).length;
              return (
                <div key={plan.id} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex flex-col gap-4 relative group hover:border-blue-300 hover:shadow-md transition-all cursor-pointer" onClick={() => setEditPlanOpen(plan)}>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-lg font-medium flex items-center gap-1">
                      <Edit2 className="w-3 h-3" /> Edit
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${plan.color} flex items-center justify-center shrink-0`}>
                      <PlanIcon id={plan.id} cls="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-base">{plan.label}</div>
                      <div className="text-xs text-gray-500">{eff.price}</div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{eff.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-gray-500 mb-0.5">Projects</div>
                      <div className="text-gray-900 font-bold">{eff.maxProjects === -1 ? "Unlimited" : eff.maxProjects}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-gray-500 mb-0.5">Users</div>
                      <div className="text-gray-900 font-bold">{eff.maxUsers === -1 ? "Unlimited" : eff.maxUsers}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">{featCount} / {ALL_FEATURE_KEYS.length} features</span>
                    <span className="text-gray-700 font-medium">{count} companies · {active} active</span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-full ${plan.color} rounded-full`} style={{ width: `${(featCount / ALL_FEATURE_KEYS.length) * 100}%` }} />
                  </div>
                  <ul className="space-y-1.5">
                    {eff.highlights.map(h => (
                      <li key={h} className="text-xs text-gray-700 flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>


          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Feature Matrix</h3>
                <p className="text-xs text-gray-500 mt-0.5">Click any plan column to edit its features</p>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500 font-medium w-64">Module / Feature</TableHead>
                  {PLANS.map(p => (
                    <TableHead key={p.id} className="text-center cursor-pointer group/th" onClick={() => setEditPlanOpen(p)}>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${p.badgeCls} inline-flex items-center gap-1`}>
                        {p.label} <Edit2 className="w-2.5 h-2.5 opacity-0 group-hover/th:opacity-100 transition-opacity" />
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {ALL_FEATURE_KEYS.map(key => (
                  <TableRow key={key} className="border-gray-100 hover:bg-gray-50">
                    <TableCell className="text-gray-700 text-sm py-3">{FEATURE_LABELS[key]}</TableCell>
                    {PLANS.map(p => {
                      const eff = getEffectivePlan(p);
                      return (
                        <TableCell key={p.id} className="text-center py-3 cursor-pointer" onClick={() => setEditPlanOpen(p)}>
                          {eff.features[key]
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                            : <Lock className="w-3.5 h-3.5 text-gray-300 mx-auto" />}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900 text-sm">Company Subscriptions</h3>
              <p className="text-xs text-gray-500 mt-0.5">Manage which plan each company is on</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500">Company</TableHead>
                  <TableHead className="text-gray-500">Current Plan</TableHead>
                  <TableHead className="text-gray-500">Status</TableHead>
                  <TableHead className="text-gray-500">Limits</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-gray-400">No companies registered yet.</TableCell></TableRow>
                )}
                {companies.map(c => {
                  const planDef = getEffectivePlan(getPlan(c.plan));
                  return (
                    <TableRow key={c.id} className="border-gray-100 hover:bg-gray-50 group">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: c.color || '#3b82f6' }}>{c.name[0]}</div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{c.name}</div>
                            <div className="text-xs text-gray-400">{c.country} · {c.currency}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${getPlan(c.plan).badgeCls}`}>{getPlan(c.plan).label}</span></TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {planDef.maxProjects === -1 ? "∞" : planDef.maxProjects} projects · {planDef.maxUsers === -1 ? "∞" : planDef.maxUsers} users
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-8 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-50 opacity-0 group-hover:opacity-100 gap-1"
                          onClick={() => setEditSubOpen(c)}>
                          <Edit2 className="w-3 h-3" /> Edit Subscription
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      );

      case "users": return (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">All Users</h2>
              <p className="text-sm text-gray-500 mt-1">{users.length} accounts across all companies</p>
            </div>
            <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl text-white shadow-lg shadow-blue-500/20">
                  <Plus className="w-4 h-4 mr-2" /> New User
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader><DialogTitle className="font-bold text-lg">Create User</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4 pt-2">
                  <div className="space-y-2"><Label>Full Name *</Label><Input name="name" required className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Email *</Label><Input name="email" type="email" required className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Password *</Label><Input name="pwd" type="password" required className="rounded-xl" /></div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Select name="companyId" defaultValue="__none__">
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select company..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No company (Super Admin)</SelectItem>
                        {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Role *</Label>
                    <Select name="role" defaultValue="Admin">
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white" disabled={createUser.isPending}>
                    {createUser.isPending ? "Creating..." : "Create User"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500">User</TableHead>
                  <TableHead className="text-gray-500">Company</TableHead>
                  <TableHead className="text-gray-500">Role</TableHead>
                  <TableHead className="text-gray-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-12 text-gray-400">No users found.</TableCell></TableRow>
                )}
                {users.map(u => {
                  const co = companies.find(c => c.id === u.companyId);
                  return (
                    <TableRow key={u.id} className="border-gray-100 hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold">
                            {u.name?.[0] || '?'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{u.name}</div>
                            <div className="text-xs text-gray-400">{u.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {co ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: co.color || '#3b82f6' }}>{co.name[0]}</div>
                            <span className="text-gray-700">{co.name}</span>
                          </div>
                        ) : <span className="text-violet-600 text-xs font-medium">Super Admin</span>}
                      </TableCell>
                      <TableCell><span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">{u.role}</span></TableCell>
                      <TableCell><Badge className="bg-emerald-50 text-emerald-700 border-none text-xs">{u.status}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      );

      case "settings": return (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Platform Settings</h2>
            <p className="text-sm text-gray-500 mt-1">System configuration and security policies</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-5 flex items-center gap-2"><Shield className="w-4 h-4 text-blue-500" /> Security</h3>
              <div className="space-y-3">
                {[
                  { label: "Session timeout", value: "8 hours" },
                  { label: "Password policy", value: "Strong (8+ chars)" },
                  { label: "Multi-factor auth", value: "Optional" },
                  { label: "Audit logging", value: "Enabled" },
                ].map(s => (
                  <div key={s.label} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-700">{s.label}</span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-500">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-5 flex items-center gap-2"><Globe className="w-4 h-4 text-emerald-500" /> Platform</h3>
              <div className="space-y-3">
                {[
                  { label: "Registration", value: "Open" },
                  { label: "Free trial", value: "Disabled" },
                  { label: "Plans", value: "3 active" },
                  { label: "Multi-tenant", value: "Enabled" },
                ].map(s => (
                  <div key={s.label} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-700">{s.label}</span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-500">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );

      default: return null;
    }
  };

  const groups = [...new Set(TABS.map(t => t.group))];

  return (
    <div className="flex h-screen bg-gray-50/80 font-sans overflow-hidden">
      <aside className={`${collapsed ? "w-[68px]" : "w-64"} bg-slate-900 flex flex-col shrink-0 transition-all duration-300`}>
        <div className={`p-4 border-b border-white/10 flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm leading-none">FinProject</div>
              <div className="text-xs text-slate-400 mt-0.5">Control Panel</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors ${collapsed ? "mt-2" : ""}`}
          >
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 px-2 py-4 space-y-4 overflow-y-auto">
          {groups.map(group => (
            <div key={group}>
              {!collapsed && (
                <div className="px-3 mb-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {GROUP_LABELS[group]}
                  </span>
                </div>
              )}
              <div className="space-y-0.5">
                {TABS.filter(t => t.group === group).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    title={collapsed ? t.label : undefined}
                    className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                      activeTab === t.id
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                        : "text-slate-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <t.icon className={`${collapsed ? "w-5 h-5" : "w-4 h-4"} shrink-0`} />
                    {!collapsed && <span>{t.label}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className={`p-3 border-t border-white/10 ${collapsed ? "" : ""}`}>
          {!collapsed ? (
            <>
              <div className="flex items-center gap-3 px-2 py-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold">SA</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{user?.name || "Super Admin"}</div>
                  <div className="text-xs text-violet-400 truncate">Super Admin</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-white/5 hover:text-rose-400 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold">SA</div>
              <button onClick={handleLogout} title="Sign Out"
                className="p-2 rounded-lg text-slate-400 hover:bg-white/5 hover:text-rose-400 transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-200/60 px-8 py-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-400">Control Panel</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            <span className="text-gray-900 font-medium">{currentTab?.label}</span>
          </div>
        </div>
        <div className="p-8">
          {renderContent()}
        </div>
      </main>

      {editSubOpen && (
        <SubscriptionEditor key={editSubOpen.id} company={editSubOpen} onClose={() => setEditSubOpen(null)} />
      )}

      {editPlanOpen && (
        <PlanEditor key={editPlanOpen.id} plan={editPlanOpen} config={getPlanConfig(editPlanOpen.id)}
          onClose={() => setEditPlanOpen(null)} onSaved={() => { refetchPlanConfigs(); setEditPlanOpen(null); }} />
      )}

    </div>
  );
}
